<script setup lang="ts">
import { ref } from "vue";

/**
 * A vertical slider beside a month chart that moves the whole pattern up or
 * down together. It only reports an offset; the chart owning the months
 * decides what moving them means — see shiftPattern() in PlanFillCpmTab and
 * PlanImpressionShareTab. Every offset is from where the months stood when the
 * slider was picked up (`start`), not from the last move, and it springs back
 * to 0 on release (`end`), ready to nudge again from the new pattern.
 *
 * Meant to sit outside the chart's scrolling area, to its right: it never adds
 * to the width that scrolls, and the label it shows while dragging reaches
 * left over the chart without being clipped.
 */
const props = defineProps<{
	/** How far it reaches either way. */
	range: number;
	step: number;
	color: string;
	/** As tall as the chart's bars. */
	height: number;
	/** The offset as the label shows it while dragging. */
	format: (offset: number) => string;
	title?: string;
	/**
	 * When set, a + above the slider and a − below it, each a nudge of this
	 * much on the pattern as it stands. The slider shrinks to make room, so
	 * the column as a whole stays as tall as the bars.
	 */
	buttonStep?: number;
}>();

const emit = defineEmits<{
	start: [];
	shift: [offset: number];
	end: [];
}>();

const offset = ref(0);

const onUpdate = (value: number) => {
	offset.value = value;
	emit("shift", value);
};

const onEnd = () => {
	offset.value = 0;
	emit("end");
};

/** Room each button takes from the slider's height. */
const BUTTON_SIZE = 24;

const sliderHeight = () =>
	props.buttonStep ? props.height - 2 * BUTTON_SIZE : props.height;

/** One click is one whole nudge: picked up, moved, and let go again. */
const nudge = (direction: 1 | -1) => {
	if (!props.buttonStep) return;

	emit("start");
	emit("shift", direction * props.buttonStep);
	emit("end");
};
</script>

<template>
	<div class="pattern-nudge" :title="props.title">
		<v-btn
			v-if="props.buttonStep"
			icon
			variant="text"
			density="compact"
			:size="BUTTON_SIZE"
			:aria-label="`Up ${props.format(props.buttonStep)}`"
			@click="nudge(1)"
		>
			<v-icon size="12" icon="fa: fa-light fa-plus" />
		</v-btn>
		<v-slider
			:model-value="offset"
			direction="vertical"
			:min="-props.range"
			:max="props.range"
			:step="props.step"
			:color="props.color"
			track-color="on-surface"
			thumb-size="14"
			thumb-label
			hide-details
			:style="{ height: sliderHeight() + 'px' }"
			@start="emit('start')"
			@update:model-value="onUpdate"
			@end="onEnd"
		>
			<template v-slot:thumb-label="{ modelValue }">
				{{ props.format(modelValue) }}
			</template>
		</v-slider>
		<v-btn
			v-if="props.buttonStep"
			icon
			variant="text"
			density="compact"
			:size="BUTTON_SIZE"
			:aria-label="`Down ${props.format(props.buttonStep)}`"
			@click="nudge(-1)"
		>
			<v-icon size="12" icon="fa: fa-light fa-minus" />
		</v-btn>
	</div>
</template>

<style scoped lang="scss">
// Stacked above the chart beside it, so the label lands on top of the bars it
// reaches over rather than underneath them.
.pattern-nudge {
	position: relative;
	z-index: 3;
	flex: 0 0 48px;
	display: flex;
	flex-direction: column;
	align-items: center;
	align-self: flex-start;
}

// Vuetify pads a vertical slider top and bottom; without it the track runs
// exactly as tall as the bars.
.pattern-nudge :deep(.v-slider.v-input--vertical) {
	margin-top: 0;
	margin-bottom: 0;
}

.pattern-nudge :deep(.v-slider.v-input--vertical > .v-input__control) {
	min-height: 0;
}

// Opens to the left, over the chart, rather than Vuetify's default right —
// the slider sits at the chart's right edge, so the right is outside it.
.pattern-nudge :deep(.v-slider-thumb__label) {
	z-index: 3;
	left: auto;
	right: calc(var(--v-slider-thumb-size) / 2);
	// One line, as wide as "−€12.50" needs: Vuetify sizes it for a bare number.
	width: auto;
	padding-inline: 6px;
	white-space: nowrap;
}

.pattern-nudge :deep(.v-slider-thumb__label > .v-slider-thumb__label-wedge) {
	clip-path: polygon(100% 50%, 50% 0, 50% 100%);
	left: auto;
	right: calc(-6px + 0.2px);
}
</style>
